import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import * as fromRoot from '../../reducers';
import { BaseApiOutput, User } from '../models';
import { environment } from 'src/environments/environment';
import { ApiService } from 'src/app/core/api.service';
import { Base64Service } from 'src/app/core/base64.service';
import { SyncCryptService } from 'src/app/core/crypt/sync-crypt.service';
import { LoggerService } from 'src/app/core/logger.service';
import { UserService } from 'src/app/core/user.service';
import { LinkPasswordService } from 'src/app/links/services/link-password.service';
import { IErrorCode, IShareData, ISharememberData } from 'src/sync';
import { BroadcastService } from './broadcast.service';
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ShareService {
  // Initialize the BehaviorSubject with default value for theme
  public isDarkThemeSubject = new BehaviorSubject<boolean>(false);
  public isDarkTheme$ = this.isDarkThemeSubject.asObservable();

  // Initialize the Subject for avatar
  private avatarUrlSubject: Subject<string> = new Subject<string>();
  public avatarUrl$: Observable<string> = this.avatarUrlSubject.asObservable();

  public user: User;
  public avatarEndpoint = `${environment.logohost}avatar/saved/`;
  public avatarUrl: string;

  private openPanelSource = new Subject<void>();
  public openPanel$ = this.openPanelSource.asObservable();
  private themeChangeEventListener = undefined;

  constructor(
    private store: Store<fromRoot.State>,
    private apiService: ApiService,
    private base64Service: Base64Service,
    private loggerService: LoggerService,
    private syncCryptService: SyncCryptService,
    private userService: UserService,
    private broadcastService: BroadcastService,
    private linkPasswordService: LinkPasswordService,
    public datePipe: DatePipe,
  ) {
    this.init();
  }

  public init(): void {
    this.store.pipe(select(fromRoot.getAuthUser)).subscribe((data) => {
      if (data) {
        this.user = data;
      } else {
        this.user = null;
      }
    });
  }

  public updateAvatarUrlWithTimestamp(timeStamp: number, isProfileRemoved: boolean): void {
    const currentUrl = `${this.avatarEndpoint}${this.user.id}`;
    const updatedUrl = currentUrl.concat(`?${timeStamp}`);
    isProfileRemoved ? this.avatarUrlSubject.next('') : this.avatarUrlSubject.next(updatedUrl);
  }

  public setDarkTheme = (isDarkTheme: boolean): void => {
    this.isDarkThemeSubject.next(isDarkTheme);
  };

  public formatTimestamp(timestamp: number): string {
    return this.datePipe.transform(timestamp, 'MMM d, y h:mm a');
  }

  public openPanel(): void {
    this.openPanelSource.next();
  }

  /**
   * @ngdoc method
   * @name  processMembers
   * @methodOf sync.service:Share
   * @description
   * Prepares the sharemember list for display in the CP
   * @return {Array}
   */
  public processMembers(people: Array<any>): Array<ISharememberData> {
    const result: Array<ISharememberData> = [];
    for (let i = 0, len = people.length; i < len; i++) {
      const p = people[i];
      result.push({
        sharememberId: p.uniqid,
        userId: p.userid,
        email: p.email,
        user_status_id: p.user_status_id, // refer to sync-core/api/lib/UserStatus
        displayname: this.base64Service.decode(p.displayname),
        status: p.status,
        pubkey: p.pubkey,
        pubkeyId: p.pubkey_id,
        shareId: p.share_id,
        permissions: p.permissions,
        isMe: p.userid == this.userService.get('id'),
        selected: false,
        num_apps: p.num_apps,
      });
    }
    return result;
  }

  /**
   * @ngdoc method
   * @name  getShare
   * @methodOf sync.service:Share
   * @description
   * Retrieves all data for a given share and set it on the view object
   * for binding to controllers/directives.
   * @param {Integer} shareId The share to retrieve
   * @return {Promise} The promise chain from the API call
   */
  public async getShare(shareId: number): Promise<IShareData> {
    const data = await this.apiService.execute('shareget', { share_id: shareId });
    const res = await this.processShare(data);
    return res;
  }

  private async processShare(data: any): Promise<IShareData> {
    const share = data.share;

    if (share && share.is_owner && share.enc_password == 'NULL' && share.share_id) {
      const linkPass = await this.linkPasswordService.makeLinkPassword(24);
      const shareData = await this.syncCryptService.mkShareData(linkPass, this.userService.get('pubkey'));
      share.enc_password = shareData.enc_password;
      await this.apiService.execute('grantinvitepermission', {
        uniqid: share.uniqid,
        share_id: share.share_id,
        enc_password: share.enc_password,
      });
    }

    const ret: IShareData = {
      people: this.processMembers(share.people),
      peoplex: this.processMembers(share.peoplex),
      syncId: share.sync_id,
      encName: share.enc_name,
      label: this.base64Service.decode(share.label),
      name: '',
      inviteStatus: share.invite_status,
      isReadOnly: share.is_readonly,
      isOwner: share.is_owner,
      isSeeOthers: share.is_seeothers,
      isInvite: share.is_invite,
      isAddApp: share.is_addapp,
      shareId: share.share_id,
      shareSequence: share.share_sequence,
      sharememberId: share.uniqid,
      encPassword: share.enc_password,
      salt: share.salt,
      iterations: share.iterations,
      app_integrated: share.app_integrated,
    };
    try {
      const name = await this.syncCryptService.filenameDecrypt(share.enc_name);
      ret.name = name;
      return ret;
    } catch (error) {
      this.loggerService.error('Unable to decrypt the file name');
      this.loggerService.error(JSON.stringify(share));
      throw { code: 2023 };
    }
  }

  /**
   * @ngdoc method
   * @name  resendInvite
   * @methodOf sync.service:Share
   * @description
   * Re-sends a share invite email to the  sharemember.
   * @param {Integer} shareId The share id
   * @param {Integer} sharememberId The sharemember's id
   * @return {Promise} The promise chain from the API call
   */
  public resendInvite(shareData: IShareData, email: string): Promise<any> {
    const inviteKeyP = shareData.encPassword
      ? this.syncCryptService.linkpasswordDecrypt(shareData.encPassword)
      : Promise.resolve('');

    return inviteKeyP.then((inviteKey) =>
      this.apiService.execute(
        'sharemailinvite',
        {
          share_id: shareData.shareId,
          emaillist: [email],
          enc_password: inviteKey,
        },
        false,
      ),
    );
  }

  /**
   * @ngdoc method
   * @name  setPermission
   * @methodOf sync.service:Share
   * @description
   * Sets the permission of a share member.  Whatever the permission is,
   * it is toggled, e.g., if it was true or set, it will be false after.
   *
   * This is a network call for instant change.
   * @param {String} perm The permission to toggle.  Defined in the
   *                      Permission class of the backend api.
   * @param {Object} member The share member to set the permission on.
   * @return {Promise} The promise chain from converting a folder to share
   */
  public async setPermission(
    shareData: IShareData,
    perm: string,
    member: ISharememberData,
  ): Promise<BaseApiOutput | IErrorCode> {
    const newVal = !member.permissions[perm] ? 1 : 0;
    this.loggerService.info(`Changing share permission ${perm} to new value ${newVal}`);

    try {
      if (['perINVITE', 'perOWNER'].includes(perm) && newVal == 1) {
        if (shareData.encPassword && member.pubkey) {
          const pass = await this.syncCryptService.linkpasswordDecrypt(shareData.encPassword);
          const newPass = await this.syncCryptService.linkpasswordEncrypt(pass, member.pubkey);

          const response = await this.apiService.execute('grantinvitepermission', {
            uniqid: member.sharememberId,
            share_id: member.shareId,
            enc_password: newPass,
          });

          if (perm === 'perINVITE') {
            return response;
          }
        } else {
          this.loggerService.error('Cannot find encPassword or member pubkey');
          this.loggerService.error(' - encPassword = ' + shareData.encPassword);
          this.loggerService.error(' - member pubkey = ' + member.pubkey);
          this.loggerService.error('Share ID: ' + shareData.shareId);
          return;
        }
        // new sharememberapi set permission
      }

      return await this.apiService.execute('sharememberpermission', {
        uniqid: member.sharememberId,
        share_id: member.shareId,
        permission: perm,
        value: newVal,
      });
    } catch (e) {
      return this.handleErr(e);
    }
  }
  private handleErr(err: any): Promise<IErrorCode> {
    let code: number;
    if (err.error_code) {
      code = err.error_code;
    } else if (err.code) {
      code = err.code;
    } else {
      code = 9000;
    }
    return Promise.reject({ code: code });
  }

  /**
   * @ngdoc method
   * @name  setPermission
   * @methodOf sync.service:Share
   * @description
   * Sets the label for a share
   * @param {Integer} shareId The share id
   * @param {String} label     The label in plain text
   * @returns {Promise} The promise from the API call
   */
  public async setLabel(shareId: number, label: string): Promise<any> {
    const labelB64 = this.base64Service.encode(label);
    try {
      const response = await this.apiService.execute(
        'sharelabel',
        {
          share_id: shareId,
          label: labelB64,
        },
        true,
      );
      this.broadcastService.broadcast('event:share-list.reload');
      await this.getShare(shareId);
      return response;
    } catch (err) {
      this.handleErr(err);
    }
  }

  /**
   * @ngdoc method
   * @name  stopShare
   * @methodOf sync.service:Share
   * @description
   * Stops a share.  Only the owner can initiate this request.  Refreshes
   * the list of shares on completion.
   * @param {Integer} shareId The share id
   * @param {Integer} sharememberId The sharemember's id
   * @param {Boolean} purge Whether to purge the share
   * @return {Promise} The promise chain from the API call
   */
  public async stopShare(shareId: number, sharememberId: number, purge: boolean): Promise<{ success: number } | any> {
    try {
      const resp = await this.apiService.execute(
        'sharestop',
        {
          sharemember_id: sharememberId,
          share_id: shareId,
          purge: purge,
        },
        false,
      );
      this.broadcastService.broadcast('event:share-list.reload');
      return resp;
    } catch (err) {
      this.handleErr(err);
    }
  }

  /**
   * @ngdoc method
   * @name  leaveShare
   * @methodOf sync.service:Share
   * @description
   * Leaves a share voluntarily.  Initiated by a user who is not the owner
   * but no longer wants to be part of the share folder.
   * @param {Integer} shareId The share id
   * @param {Integer} sharememberId The sharemember's id
   * @return {Promise} The promise chain from the API call
   */
  public async leaveShare(shareId: number, sharememberId: number): Promise<any> {
    try {
      await this.apiService.execute('sharememberleave', {
        share_id: shareId,
        sharemember_id: sharememberId,
        bg: 1, //use background job 1 = true, i.e. leave share button from manage share
      });
      this.broadcastService.broadcast('event:share-list.reload');
      return true;
    } catch (err) {
      this.handleErr(err);
    }
  }
  public updateTheme = (): void => {
    const theme = this.userService.get('theme');

    if (!theme) {
      const darkThemeMq = window.matchMedia('(prefers-color-scheme: dark)');
      this.setDarkTheme(darkThemeMq.matches);
      if (!this.themeChangeEventListener) {
        this.themeChangeEventListener = (e): void => this.setDarkTheme(e.matches);
        darkThemeMq.addEventListener('change', this.themeChangeEventListener);
      }
    } else {
      this.themeChangeEventListener = undefined;
      this.setDarkTheme(theme === 'dark');
    }
  };
}
